import { FormEvent, useEffect, useMemo, useState } from 'react';
import { api } from '../../../../lib/api';
import {
  collections,
  type CollectionKey,
  type Row,
} from '../admin-collections.config';



interface EditorProps {
  name: CollectionKey;
}

export function Editor({ name }: EditorProps) {
  const meta = collections[name];
  const [rows, setRows] = useState<Row[]>([]);
  const [editing, setEditing] = useState<Row | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const empty = useMemo<Row>(
    () =>
      Object.fromEntries(
        meta.fields.map((f) => [f.key, f.type === 'checkbox' ? 1 : ''])
      ),
    [meta.fields]
  );

  const load = async (): Promise<void> => {
    try {
      const data: Row[] = await api.adminCollection(name);
      setRows(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar dados.');
    }
  };

  useEffect(() => {
    void load();
  }, [name]);

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!editing) return;

    setSaving(true);
    setError('');

    try {
      const payload: Row = { ...editing };
      delete payload.id;

      if (editing.id) {
        await api.update(name, String(editing.id), payload);
      } else {
        await api.create(name, payload);
      }

      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível salvar.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string): Promise<void> {
    if (!window.confirm('Excluir este item?')) return;
    try {
      await api.remove(name, id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível excluir.');
    }
  }

  return (
    <section className="admin-section" aria-labelledby={`${name}-title`}>
      <div className="admin-section-head">
        <div>
          <span className="section-tag">CMS</span>
          <h2 id={`${name}-title`}>{meta.title}</h2>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setEditing({ ...empty })}>
          Novo
        </button>
      </div>

      {editing && (
        <form className="admin-editor" onSubmit={save}>
          <div className="admin-form-grid">
            {meta.fields.map((field) => (
              <label key={field.key} className={field.type === 'textarea' ? 'span-2' : ''}>
                {field.label}
                {field.type === 'textarea' ? (
                  <textarea
                    rows={5}
                    value={String(editing[field.key] ?? '')}
                    onChange={(e) =>
                      setEditing({ ...editing, [field.key]: e.target.value })
                    }
                  />
                ) : field.type === 'checkbox' ? (
                  <input
                    type="checkbox"
                    checked={Boolean(Number(editing[field.key] ?? 0))}
                    onChange={(e) =>
                      setEditing({ ...editing, [field.key]: e.target.checked ? 1 : 0 })
                    }
                  />
                ) : (
                  <input
                    type={field.type ?? 'text'}
                    value={String(editing[field.key] ?? '')}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        [field.key]:
                          field.type === 'number'
                            ? Number(e.target.value)
                            : e.target.value,
                      })
                    }
                  />
                )}
              </label>
            ))}
          </div>
          <div className="admin-form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setEditing(null)}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {error && <p className="form-error" role="alert">{error}</p>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Status</th>
              <th>Ordem</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={String(row.id ?? '')}>
                <td>
                  <strong>{row.name ?? row.label ?? row.question ?? row.href ?? row.id}</strong>
                  <small>{row.slug ?? row.description ?? ''}</small>
                </td>
                <td>{Number(row.active) ? 'Ativo' : 'Oculto'}</td>
                <td>{row.sort_order ?? '—'}</td>
                <td className="table-actions">
                  <button type="button" className="btn btn-small btn-outline" onClick={() => setEditing({ ...row })}>
                    Editar
                  </button>
                  <button type="button" className="btn btn-small btn-danger" onClick={() => void remove(String(row.id))}>
                    Excluir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}